function debounce(fn, wait = 500) {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn.apply(this, args), wait);
  };
}

class CartServicesSelector extends HTMLElement {
  #container;
  #lineIndex;
  #loader;
  #debouncedSync;

  constructor() {
    super();
    this.#initialize();
    this.#debouncedSync = debounce(this.#syncServicesToCart.bind(this), 300);
  }

  #initialize() {
    this.#container = this.querySelector('.product-services__groups');
    if (!this.#container) return;

    this.#lineIndex = this.#container.dataset.lineIndex;
    this.#loader = this.querySelector('.cart-item-services__loader');

    this.#container.addEventListener('change', (evt) => this.#onCheckboxChange(evt));
  }

  async #onCheckboxChange(evt) {
    const checkbox = evt.target;
    if (!checkbox.classList.contains('cart-service-checkbox')) return;

    this.#ensureSingleSelectionPerGroup(checkbox);
    this.#debouncedSync();
  }

  #ensureSingleSelectionPerGroup(selectedCheckbox) {
    if (!selectedCheckbox.checked) return;

    const groupName = selectedCheckbox.dataset.group;
    const groupCheckboxes = this.#container.querySelectorAll(`input[data-group="${groupName}"]`);

    groupCheckboxes.forEach((checkbox) => {
      if (checkbox !== selectedCheckbox) {
        checkbox.checked = false;
      }
    });
  }

  async #syncServicesToCart() {
    this.#showLoadingState();

    try {
      const properties = this.#buildPropertiesFromSelectedServices();
      await this.#updateLineItemProperties(properties);
    } catch (error) {
      console.error('Failed to update cart services:', error);
    } finally {
      this.#hideLoadingState();
    }
  }

  #buildPropertiesFromSelectedServices() {
    const selectedCheckboxes = this.#container.querySelectorAll('input.cart-service-checkbox:checked');
    const properties = {};
    const servicesForFunctions = [];
    let index = 1;

    selectedCheckboxes.forEach((checkbox) => {
      const { variantId, variantSku, serviceTitle } = checkbox.dataset;

      properties[`_service_title_${index}`] = serviceTitle;
      properties[`_service_sku_${index}`] = variantSku;

      servicesForFunctions.push({
        id: variantId,
        sku: variantSku,
        title: serviceTitle
      });

      index++;
    });

    this.#clearUnusedServiceProperties(properties, index);
    properties['_services'] = servicesForFunctions.length > 0 
      ? JSON.stringify(servicesForFunctions) 
      : '';

    return properties;
  }

  #clearUnusedServiceProperties(properties, startIndex) {
    const maxServices = 10;
    for (let i = startIndex; i <= maxServices; i++) {
      properties[`_service_title_${i}`] = '';
      properties[`_service_sku_${i}`] = '';
    }
  }

  async #updateLineItemProperties(properties) {
    const sections = this.#getSectionsToUpdate();
    
    // Get current quantity to preserve it
    const lineItem = this.closest('.cart-item');
    const quantityInput = lineItem?.querySelector('.qty-input__input');
    const currentQuantity = quantityInput ? parseInt(quantityInput.value, 10) : 1;

    const response = await fetch(theme.routes.cartChange, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        line: parseInt(this.#lineIndex),
        quantity: currentQuantity,
        properties,
        sections,
        sections_url: window.location.pathname
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    this.#refreshPageSections(data.sections);
    this.#dispatchServicesUpdatedEvent(data);

    return data;
  }

  #getSectionsToUpdate() {
    const sections = ['cart-icon-bubble', 'free-shipping-notice'];

    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      sections.push(this.#getSectionId(cartDrawer));
    }

    const cartItems = document.querySelector('cart-items');
    if (cartItems?.dataset.section) {
      sections.push(cartItems.dataset.section);
    }

    return sections;
  }

  #getSectionId(element) {
    return element.closest('.shopify-section').id.replace('shopify-section-', '');
  }

  #refreshPageSections(sections) {
    if (!sections) return;

    this.#refreshCartIconBubble(sections);
    this.#refreshFreeShippingNotice(sections);
    this.#refreshCartDrawer(sections);
    this.#refreshCartPage(sections);
  }

  #refreshCartIconBubble(sections) {
    const cartIconBubble = document.getElementById('cart-icon-bubble');
    if (cartIconBubble && sections['cart-icon-bubble']) {
      cartIconBubble.innerHTML = sections['cart-icon-bubble'];
    }
  }

  #refreshFreeShippingNotice(sections) {
    const freeShippingNotice = document.getElementById('free-shipping-notice');
    if (!freeShippingNotice || !sections['free-shipping-notice']) return;

    const newNotice = this.#parseHTML(sections['free-shipping-notice'])
      .querySelector('.free-shipping-notice');

    if (newNotice) {
      freeShippingNotice.innerHTML = newNotice.innerHTML;
    }
  }

  #refreshCartDrawer(sections) {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return;

    const sectionId = this.#getSectionId(cartDrawer);
    if (!sections[sectionId]) return;

    const doc = this.#parseHTML(sections[sectionId]);

    this.#updateElementContent(
      cartDrawer.querySelector('cart-items'),
      doc.querySelector('cart-items')
    );

    this.#updateElementContent(
      cartDrawer.querySelector('.cart-drawer__summary'),
      doc.querySelector('.cart-drawer__summary')
    );
  }

  #refreshCartPage(sections) {
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) return;

    this.#refreshCartPageItems(sections);
    this.#refreshCartPageSummary(sections);
  }

  #refreshCartPageItems(sections) {
    const cartItems = document.getElementById('cart-items')?.closest('cart-items');
    if (!cartItems) return;

    const sectionId = cartItems.dataset.section;
    if (!sections[sectionId]) return;

    const newContent = this.#parseHTML(sections[sectionId]).querySelector('cart-items');
    if (newContent) {
      cartItems.innerHTML = newContent.innerHTML;
    }
  }

  #refreshCartPageSummary(sections) {
    const cartSummary = document.getElementById('cart-summary');
    if (!cartSummary) return;

    const sectionId = cartSummary.dataset.section;
    if (!sections[sectionId]) return;

    const newSummary = this.#parseHTML(sections[sectionId]).getElementById('cart-summary');
    if (newSummary) {
      cartSummary.innerHTML = newSummary.innerHTML;
    }
  }

  #parseHTML(html) {
    const parser = new DOMParser();
    return parser.parseFromString(html, 'text/html');
  }

  #updateElementContent(currentElement, newElement) {
    if (currentElement && newElement) {
      currentElement.innerHTML = newElement.innerHTML;
    }
  }

  #dispatchServicesUpdatedEvent(cartData) {
    this.dispatchEvent(new CustomEvent('on:cart:services-updated', {
      bubbles: true,
      detail: { cart: cartData }
    }));
  }

  #showLoadingState() {
    this.#loader?.classList.remove('hidden');
    this.classList.add('pointer-events-none', 'opacity-70');
  }

  #hideLoadingState() {
    this.#loader?.classList.add('hidden');
    this.classList.remove('pointer-events-none', 'opacity-70');
  }
}

if (!customElements.get('cart-services-selector')) {
  customElements.define('cart-services-selector', CartServicesSelector);
}
